import React, { useState, useEffect, useContext } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, Label, FormGroup, Input } from 'reactstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import LoadingButton from '../../../common/LoadingButton';
import { assignToApplication, unassignFromApplication, getRoles, getClients } from '../../../../api/users';
import { CurrentUserContext } from '../../../../context/current-user-context';
import UserApplications from './UserApplications';
import AdminUserApplications from './AdminUserApplications';
import { translateRoleName } from '../../../../utils/helpers';

export default function UserSettingsModal({
    availableClientApps,
    availableClients,
    isOpen,
    toggle,
    className,
    editUserSettings,
    resetPassword,
    PENDING_EDIT_USER_SETTINGS,
    PENDING_RESET_PASSWORD,
    selectedUser,
}) {
    const context = useContext(CurrentUserContext);

    const hasAdminRole = context.currentUser.role === 'admin';
    const [grantedApplications, setGrantedApplications] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [selectedUserRole, setSelectedUserRole] = useState('');
    const [clientApps, setClientApps] = useState([]);
    const [clients, setClients] = useState(availableClients);
    const [selectedClient, setSelectedClient] = useState('');
    const [empty, setEmpty] = useState(false);

    const availableUserApplications = context.currentUser.applications;

    useEffect(() => {
        if (isOpen) {
            setGrantedApplications(selectedUser.userApplications);
            setSelectedUserRole(selectedUser.role);
            setSelectedClient(selectedUser.client);
        }
    }, [isOpen]);

    useEffect(() => {
        getRoles().then((res) => {
            setAvailableRoles(res.data.data);
        });
    }, []);

    useEffect(() => {
        getClients()
            .then((res) => {
                setClients(res.data.data);
            })
            .catch((err) => {
                if (err.status === 500) {
                    console.error(err);
                }
            });
    }, []);

    const toggleMethod = () => {
        toggle();
    };

    const isCheckboxDisabled = () => {
        return selectedUser && selectedUser.id === context.currentUser.id;
    };

    const checkApplicationsChanges = () => {
        // eslint-disable-next-line
        selectedUser.applications.map((app) => {
            if (!Array.from(grantedApplications).includes(app)) {
                unassignFromApplication(selectedUser.id, { application: app });
            }
        });
        // eslint-disable-next-line
        Array.from(grantedApplications).map((app) => {
            if (!selectedUser.applications.includes(app)) {
                assignToApplication(selectedUser.id, { application: app });
            }
        });
    };

    const onSubmitHandler = async (values) => {
        // await checkApplicationsChanges();
        await editUserSettings(
            {
                newFirstName: values.firstName,
                newLastName: values.lastName,
                newRole: selectedUserRole,
                newCompany: values.company ? values.company : null,
                isDisabled: values.isDisabled,
                newUserApplications: grantedApplications,
                newClient: selectedClient,
            }
        );
    };

    const handleResetPassword = () => {
        resetPassword({ username: selectedUser.username });
    };

    const validationSchema = Yup.object().shape({
        username: Yup.string(),
        firstName: Yup.string(),
        lastName: Yup.string(),
        company: Yup.string(),
        client: Yup.string(),
    });

    const updateApplicationsAccess = (newArray) => {
        setGrantedApplications(newArray);
    };

    const updateSelectedClient = (e) => {
        setSelectedClient(e.target.value);
        setGrantedApplications([]);
        const selectedElement = clients.find((el) => el.client === e.target.value);
        setClientApps(selectedElement.applications);
    };

    useEffect(() => {
        if (selectedClient) {
            const selectedElement = clients.find((el) => el.client === selectedClient);
            setClientApps(selectedElement.applications);
        }
    }, [selectedClient]);

    const updateSelectedUserRole = (e) => {
        setSelectedUserRole(e.target.value);

        if (e.target.value === 'admin'){
            setEmpty(true);
        } else {
            setEmpty(false);
            setGrantedApplications([]);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggleMethod} className={className}>
            <ModalHeader toggle={toggleMethod}>Edit user</ModalHeader>
            <Formik
                enableReinitialize={true}
                initialValues={{
                    firstName: (selectedUser && selectedUser.firstName) || '',
                    lastName: (selectedUser && selectedUser.lastName) || '',
                    company: (selectedUser && selectedUser.company) || '',
                    role: (selectedUser && selectedUser.role) || 'user',
                    isDisabled: (selectedUser && selectedUser.isDisabled) || false,
                }}
                onSubmit={(values, actions) => {
                    onSubmitHandler(values, actions);
                }}
                validationSchema={validationSchema}
                validateOnChange={false}
                render={({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                    <Form onSubmit={handleSubmit}>
                        <ModalBody>
                            <FormGroup className='mb-0'>
                                <Label for='firstName'>
                                    First name <span className='text-danger'>*</span>
                                </Label>
                                <Input
                                    type='text'
                                    id='firstName'
                                    placeholder='First name...'
                                    name='firstName'
                                    onChange={handleChange}
                                    value={values.firstName}
                                    required
                                />
                                {errors.firstName ? (
                                    <div className='small text-danger'>{errors.firstName}</div>
                                ) : (
                                    <div style={{ height: '19px' }} />
                                )}
                            </FormGroup>
                            <FormGroup className='mb-0'>
                                <Label for='lastName'>
                                    Last name <span className='text-danger'>*</span>
                                </Label>
                                <Input
                                    type='text'
                                    id='lastName'
                                    placeholder='Last name...'
                                    name='lastName'
                                    onChange={handleChange}
                                    value={values.lastName}
                                    required
                                />
                                {errors.lastName ? (
                                    <div className='small text-danger'>{errors.lastName}</div>
                                ) : (
                                    <div style={{ height: '19px' }} />
                                )}
                            </FormGroup>
                            <FormGroup className='mb-0'>
                                <Label for='company'>Company</Label>
                                <Input
                                    type='text'
                                    id='company'
                                    placeholder='Company...'
                                    name='company'
                                    onChange={handleChange}
                                    value={values.company}
                                />
                                {errors.company ? (
                                    <div className='small text-danger'>{errors.company}</div>
                                ) : (
                                    <div style={{ height: '19px' }} />
                                )}
                            </FormGroup>

                            <FormGroup className='mb-0'>
                                <Label for='client'>Client</Label>
                                <Input
                                    disabled={true}
                                    id='company'
                                    type='select'
                                    placeholder='Select client...'
                                    name='client'
                                    value={selectedClient}
                                    onChange={updateSelectedClient}
                                >
                                    {clients.map((client) => (
                                        <option key={client.client} value={client.client}>
                                            {client.client}
                                        </option>
                                    ))}
                                </Input>
                                {errors.client ? (
                                    <div className='small text-danger'>{errors.client}</div>
                                ) : (
                                    <div style={{ height: '19px' }} />
                                )}
                            </FormGroup>

                            <FormGroup className='mb-0'>
                                <Label for='Role'>Role</Label>
                                <Input
                                    disabled={!availableRoles.userRoles.includes(selectedUserRole) || context.currentUser.role!=='admin'}
                                    id='role'
                                    type='select'
                                    placeholder='Select role...'
                                    name='role'
                                    value={selectedUserRole}
                                    onChange={updateSelectedUserRole}
                                >
                                    {!availableRoles.userRoles.includes(selectedUserRole) ? (
                                         <option key={selectedUserRole} value={selectedUserRole}>{translateRoleName(selectedUserRole)}</option>
                                    ): (
                                    availableRoles.userRoles.map((role) => (
                                        <option key={role} value={role}>
                                            {translateRoleName(role)}
                                        </option>
                                    )))}
                                </Input>
                                {errors.role ? (
                                    <div className='small text-danger'>{errors.role}</div>
                                ) : (
                                    <div style={{ height: '19px' }} />
                                )}
                            </FormGroup>

                            {selectedUserRole !== 'admin' && (
                                <>
                                    <FormGroup className='mb-0'>
                                        <Label for='applicationCheckboxes'>Applications</Label>
                                    </FormGroup>
                                    {empty && selectedUser.role === 'admin' ? (
                                        <AdminUserApplications
                                            update={updateApplicationsAccess}
                                            availableApplications={clientApps}
                                            userApplications={selectedUser.userApplications}
                                            availableRoles={availableRoles.applicationUserRoles}
                                        />
                                    ) : (
                                        <UserApplications
                                            update={updateApplicationsAccess}
                                            availableApplications={
                                                hasAdminRole ? availableClientApps : availableUserApplications
                                            }
                                            userApplications={selectedUser.userApplications}
                                            availableRoles={availableRoles.applicationUserRoles}
                                        />
                                    )}
                                </>
                            )}
                                <>
                                    <FormGroup className='mt-4 mb-0'>
                                        <Label for='applicationCheckboxes'>Access</Label>
                                    </FormGroup>
                                    <FormGroup className='mb-0' check>
                                        <Label check>
                                            <Input
                                                disabled={isCheckboxDisabled()}
                                                type='checkbox'
                                                name='isDisabled'
                                                onChange={handleChange}
                                                checked={values.isDisabled}
                                            />{' '}
                                            Disabled
                                        </Label>
                                    </FormGroup>
                                </>
                        </ModalBody>
                        <ModalFooter>
                            <LoadingButton
                                loading={PENDING_RESET_PASSWORD}
                                disabled={PENDING_RESET_PASSWORD}
                                color='danger'
                                className='modal-button-reset'
                                onClick={handleResetPassword}
                            >
                                Reset password
                            </LoadingButton>{' '}
                            <LoadingButton
                                loading={PENDING_EDIT_USER_SETTINGS}
                                disabled={PENDING_EDIT_USER_SETTINGS || (empty && grantedApplications.length === 0)}
                                color="c-primary"
                                type='submit'
                            >
                                Submit changes
                            </LoadingButton>{' '}
                            <Button color='c-secondary' onClick={toggleMethod}>
                                Cancel
                            </Button>
                        </ModalFooter>
                    </Form>
                )}
            />
        </Modal>
    );
}
